"use client";

import { useState } from "react";
import { motion } from "framer-motion";
import {
  X,
  Shield,
  Calendar,
  Building,
  Key,
  FileText,
  RefreshCw,
  Copy,
  CheckCircle,
  XCircle,
  Clock,
  AlertTriangle,
} from "lucide-react";
import toast from "react-hot-toast";

interface ConsentDetailsModalProps {
  consent: any;
  onClose: () => void;
  onRefresh: () => void;
}

export default function ConsentDetailsModal({
  consent,
  onClose,
  onRefresh,
}: ConsentDetailsModalProps) {
  const [refreshing, setRefreshing] = useState(false);

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard!`);
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "Approved":
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case "Rejected":
        return <XCircle className="w-6 h-6 text-red-500" />;
      case "Expired":
        return <AlertTriangle className="w-6 h-6 text-orange-500" />;
      default:
        return <Clock className="w-6 h-6 text-yellow-500" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "Approved":
        return "bg-green-100 text-green-800 border-green-200";
      case "Rejected":
        return "bg-red-100 text-red-800 border-red-200";
      case "Expired":
        return "bg-orange-100 text-orange-800 border-orange-200";
      default:
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await onRefresh();
    setRefreshing(false);
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
          <div className="flex items-center">
            <div className="flex items-center justify-center w-12 h-12 bg-blue-100 rounded-full mr-4">
              {getStatusIcon(consent.consentStatus)}
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900">
                Consent Details
              </h2>
              <p className="text-gray-600">ID: {consent.consentId}</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={handleRefresh}
              disabled={refreshing}
              className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors disabled:opacity-50"
              title="Refresh Status"
            >
              <RefreshCw
                size={20}
                className={refreshing ? "animate-spin" : ""}
              />
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X size={24} className="text-gray-500" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 p-6 overflow-y-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Left Column - Basic Info */}
            <div className="space-y-6">
              {/* Status Card */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  Consent Status
                </h3>
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    {getStatusIcon(consent.consentStatus)}
                    <span
                      className={`ml-3 px-3 py-1 text-sm font-medium rounded-full border ${getStatusColor(
                        consent.consentStatus
                      )}`}
                    >
                      {consent.consentStatus}
                    </span>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-500">Last Updated</p>
                    <p className="text-sm font-medium text-gray-900">
                      {new Date(consent.lastUpdated).toLocaleString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Partner Information */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Building className="w-5 h-5 mr-2" />
                  Partner Information
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Partner Name:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {consent.partnerName}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Partner ID:</span>
                    <div className="flex items-center">
                      <span className="text-sm font-medium text-gray-900 mr-2">
                        {consent.partnerId}
                      </span>
                      <button
                        onClick={() =>
                          copyToClipboard(consent.partnerId, "Partner ID")
                        }
                        className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                      >
                        <Copy size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* Document Status */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Document Status
                </h3>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">
                    Document Uploaded:
                  </span>
                  <div className="flex items-center">
                    {consent.documentUploaded === "Y" ? (
                      <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
                    ) : (
                      <XCircle className="w-5 h-5 text-red-500 mr-2" />
                    )}
                    <span
                      className={`px-2 py-1 text-xs font-medium rounded-full ${
                        consent.documentUploaded === "Y"
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {consent.documentUploaded === "Y"
                        ? "Uploaded"
                        : "Not Uploaded"}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column - Technical Details */}
            <div className="space-y-6">
              {/* API Access */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Key className="w-5 h-5 mr-2" />
                  API Access
                </h3>
                <div className="space-y-3">
                  <div>
                    <p className="text-sm text-gray-600 mb-2">
                      Authorized APIs:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {consent.apiList.map((api: string, index: number) => (
                        <span
                          key={index}
                          className="px-3 py-1 text-xs bg-blue-100 text-blue-800 rounded-full border border-blue-200"
                        >
                          {api}
                        </span>
                      ))}
                    </div>
                  </div>
                  {consent.netBankingUserId && (
                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                      <span className="text-sm text-gray-600">
                        Net Banking User ID:
                      </span>
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-gray-900 mr-2">
                          {consent.netBankingUserId}
                        </span>
                        <button
                          onClick={() =>
                            copyToClipboard(
                              consent.netBankingUserId,
                              "Net Banking User ID"
                            )
                          }
                          className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <Copy size={14} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Token Information */}
              {(consent.accessToken || consent.refreshToken) && (
                <div className="bg-gray-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                    <Key className="w-5 h-5 mr-2" />
                    Token Information
                  </h3>
                  <div className="space-y-3">
                    {consent.accessToken && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">
                            Access Token:
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                consent.accessToken,
                                "Access Token"
                              )
                            }
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                        <p className="text-xs font-mono bg-white p-2 rounded border break-all">
                          {consent.accessToken.substring(0, 50)}...
                        </p>
                        {consent.accessTokenExpiresAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            Expires:{" "}
                            {new Date(
                              consent.accessTokenExpiresAt
                            ).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                    {consent.refreshToken && (
                      <div>
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm text-gray-600">
                            Refresh Token:
                          </span>
                          <button
                            onClick={() =>
                              copyToClipboard(
                                consent.refreshToken,
                                "Refresh Token"
                              )
                            }
                            className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            <Copy size={14} />
                          </button>
                        </div>
                        <p className="text-xs font-mono bg-white p-2 rounded border break-all">
                          {consent.refreshToken.substring(0, 50)}...
                        </p>
                        {consent.refreshTokenExpiresAt && (
                          <p className="text-xs text-gray-500 mt-1">
                            Expires:{" "}
                            {new Date(
                              consent.refreshTokenExpiresAt
                            ).toLocaleString()}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Timeline */}
              <div className="bg-gray-50 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Timeline
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Created:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(consent.createdAt).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Consent Date:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(consent.consentCreationDate).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">Last Updated:</span>
                    <span className="text-sm font-medium text-gray-900">
                      {new Date(consent.lastUpdated).toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 flex justify-end space-x-4 p-6 border-t border-gray-200 bg-gray-50">
          <button
            onClick={onClose}
            className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Close
          </button>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {refreshing ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                Refreshing...
              </>
            ) : (
              <>
                <RefreshCw size={16} className="mr-2" />
                Refresh Status
              </>
            )}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
}
